<script setup>
import {
  computed,
  ref,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationCard from 'dashboard/components-next/Conversation/ConversationCard/ConversationCard.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const contactsById = useMapGetter('contacts/getContactById');
const stateInbox = useMapGetter('inboxes/getInboxById');
const accountLabels = useMapGetter('labels/getLabels');
const hasMoreConversations = useMapGetter(
  'contactConversations/getHasMoreConversations'
);

const accountLabelsValue = computed(() => accountLabels.value);

const uiFlags = useMapGetter('contactConversations/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);

const contactConversations = computed(() =>
  conversations.value(route.params.contactId)
);

const loadMoreTrigger = ref(null);
let observer = null;

const loadMore = () => {
  const { contactId } = route.params;
  if (
    !contactId ||
    !hasMoreConversations.value(contactId) ||
    uiFlags.value.isFetchingList
  ) {
    return;
  }
  store.dispatch('contactConversations/fetchPrevious', contactId);
};

const observeTrigger = () => {
  if (observer && loadMoreTrigger.value)
    observer.observe(loadMoreTrigger.value);
};

onMounted(() => {
  observer = new IntersectionObserver(
    entries => {
      if (entries[0].isIntersecting) loadMore();
    },
    { threshold: 1.0 }
  );
  observeTrigger();
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});

watch(
  () => contactConversations.value.length,
  () => nextTick(observeTrigger)
);
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div
    v-else-if="contactConversations.length > 0"
    class="px-6 py-4 divide-y divide-n-strong [&>*:hover]:!border-y-transparent [&>*:hover+*]:!border-t-transparent"
  >
    <ConversationCard
      v-for="conversation in contactConversations"
      :key="conversation.id"
      :conversation="conversation"
      :contact="contactsById(conversation.meta.sender.id)"
      :state-inbox="stateInbox(conversation.inboxId)"
      :account-labels="accountLabelsValue"
      class="rounded-none hover:rounded-xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
    />
    <div ref="loadMoreTrigger" class="w-full h-px !border-transparent" />
    <div
      v-if="uiFlags.isFetchingList"
      class="flex items-center justify-center py-3 !border-transparent"
    >
      <Spinner />
    </div>
  </div>
  <p v-else class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11">
    {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.EMPTY_STATE') }}
  </p>
</template>
